import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAppContext } from '../../context/AppContext';
import { parseLocalDate, detectOutlierVotes } from '../../utils/calculations';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import { Search, Trash2, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, FlaskConical, RefreshCw, Eye } from 'lucide-react';
import logger from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const calcCommunityRating = (history) => {
  if (!history || history.length === 0) return null;
  const sum = history.reduce((a, h) => a + h.rating, 0);
  return Math.round((sum / history.length) * 10) / 10;
};

const fmtDate = (dateStr) =>
  parseLocalDate(dateStr).toLocaleDateString('pt-BR', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
  });

const summarizePlayerStats = (playerStats = {}) => {
  const entries = Object.entries(playerStats || {});
  return {
    entriesCount: entries.length,
    nonZeroPlayers: entries.filter(([, stats]) => (stats?.goals || 0) > 0 || (stats?.assists || 0) > 0).length,
    totalGoals: entries.reduce((sum, [, stats]) => sum + (stats?.goals || 0), 0),
    totalAssists: entries.reduce((sum, [, stats]) => sum + (stats?.assists || 0), 0),
  };
};

const fmtScore = (score) => (score?.teamA ?? 0) + ' x ' + (score?.teamB ?? 0);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const FixDuplicatesPage = () => {
  const navigate = useNavigate();
  const { groups, userId, profile } = useAppContext();

  const myGroups = groups.filter(
    (g) => g.ownerId === userId || (g.admins || []).includes(userId)
  );

  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null); // { dupGames, dupRatings, voteDiscrepancies }
  const [fixLog, setFixLog] = useState([]);
  const [fixing, setFixing] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState({});

  // ─── Vote viewer ───────────────────────────────────────────────────────────
  const [vvGroupId, setVvGroupId]         = useState('');
  const [vvGames, setVvGames]             = useState([]);
  const [vvGamesLoading, setVvGamesLoading] = useState(false);
  const [vvGameId, setVvGameId]           = useState('');
  const [vvData, setVvData]               = useState(null);
  const [vvLoading, setVvLoading]         = useState(false);

  // ─── Seed (apenas dev) ────────────────────────────────────────────────────
  const isDev = process.env.NODE_ENV === 'development';
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedLog, setSeedLog] = useState([]);

  const handleSeed = useCallback(async () => {
    setSeedRunning(true);
    setSeedLog([]);
    const log = (msg) => setSeedLog(prev => [...prev, msg]);

    try {
      const fmt = (d) => d.toISOString().split('T')[0];
      const today = new Date();
      const nextWeek  = new Date(today); nextWeek.setDate(today.getDate() + 7);
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      const lastWeek  = new Date(today); lastWeek.setDate(today.getDate() - 7);
      const twoWeeks  = new Date(today); twoWeeks.setDate(today.getDate() - 14);

      const groupRef = await addDoc(collection(db, 'groups'), {
        name: '[SEED] Pelada de Teste',
        sport: 'futebol',
        ownerId: userId,
        admins: [userId],
        memberIds: [userId],
        isPrivate: false,
        city: 'Porto Alegre',
        courtName: 'Campo do Seed',
        courtAddress: 'Rua Teste, 123',
        dayOfWeek: 'qua',
        defaultTime: '20:00',
        defaultMaxPlayers: 10,
        defaultPrice: 25,
        hasOpenSpots: false,
        pendingRequests: [],
        scoringConfig: {
          enabled: true, win: 3, draw: 1, absencePenalty: -1,
          leaderZone: 2, relegationZone: 2,
        },
        createdAt: serverTimestamp(),
      });
      log(`✅ Grupo criado: ${groupRef.id}`);

      const gamesCol = collection(db, 'groups', groupRef.id, 'games');
      const meObj = { id: userId, name: 'Você (seed)', position: 'MEI' };

      const g1 = await addDoc(gamesCol, {
        date: fmt(nextWeek), time: '20:00',
        maxPlayers: 10, playersPerTeam: 5, pricePerPerson: 25,
        status: 'scheduled',
        confirmedIds: [userId], absentIds: [],
        createdAt: serverTimestamp(),
      });
      log(`✅ Jogo scheduled: ${g1.id} (${fmt(nextWeek)})`);

      const g2 = await addDoc(gamesCol, {
        date: fmt(yesterday), time: '20:00',
        maxPlayers: 10, playersPerTeam: 5, pricePerPerson: 25,
        status: 'voting',
        confirmedIds: [userId], absentIds: [],
        teams: { teamA: [meObj], teamB: [] },
        score: { teamA: 3, teamB: 2 },
        drawConfig: { mode: 'rating', ratingPeriod: 'lastMonth', playersPerTeam: 5 },
        createdAt: serverTimestamp(),
      });
      log(`✅ Jogo voting: ${g2.id} (${fmt(yesterday)})`);

      const votes = {
        [userId]: { [userId]: 8 },
        'seed_voter_a': { [userId]: 7 },
        'seed_voter_b': { [userId]: 9 },
        'seed_voter_c': { [userId]: 6 },
        'seed_voter_d': { [userId]: 10 },
      };
      const g3 = await addDoc(gamesCol, {
        date: fmt(lastWeek), time: '20:00',
        maxPlayers: 10, playersPerTeam: 5, pricePerPerson: 25,
        status: 'finished',
        confirmedIds: [userId], absentIds: [],
        teams: { teamA: [meObj], teamB: [] },
        score: { teamA: 2, teamB: 1 },
        votes,
        playerStats: { [userId]: { goals: 2, assists: 1 } },
        createdAt: serverTimestamp(),
      });
      log(`✅ Jogo finished: ${g3.id} (${fmt(lastWeek)}) — 5 votos`);

      const g4 = await addDoc(gamesCol, {
        date: fmt(twoWeeks), time: '20:00',
        maxPlayers: 10, playersPerTeam: 5, pricePerPerson: 25,
        status: 'finished',
        confirmedIds: [userId], absentIds: [],
        teams: { teamA: [meObj], teamB: [] },
        score: { teamA: 1, teamB: 1 },
        createdAt: serverTimestamp(),
      });
      log(`✅ Jogo finished (sem votos): ${g4.id} (${fmt(twoWeeks)})`);

      const pastCancel = new Date(today); pastCancel.setDate(today.getDate() - 3);
      const g5 = await addDoc(gamesCol, {
        date: fmt(pastCancel), time: '20:00',
        maxPlayers: 10, playersPerTeam: 5, pricePerPerson: 25,
        status: 'cancelled',
        confirmedIds: [], absentIds: [userId],
        createdAt: serverTimestamp(),
      });
      log(`✅ Jogo cancelled: ${g5.id} (${fmt(pastCancel)})`);

      log('');
      log('🎉 Seed concluído! Volte para a tela inicial e puxe para baixo para atualizar.');
    } catch (err) {
      log(`❌ Erro: ${err.message}`);
      logger.error('Seed error:', err);
    } finally {
      setSeedRunning(false);
    }
  }, [userId]);

  // ─── Scan ──────────────────────────────────────────────────────────────────

  const handleScan = useCallback(async () => {
    setScanning(true);
    setResults(null);
    setFixLog([]);

    try {
      const dupGames = [];
      const dupRatings = [];
      const voteDiscrepancies = [];   // ← novo
      const statLossSuspects = [];
      const scannedPlayers = new Set();
      // cache de perfis já lidos: uid → { history, name, communityRating, gamesPlayed, totalGoals, totalAssists }
      const profileCache = {};
      // Acumula stats esperadas a partir de jogos finalizados: uid → { games, goals, assists, name }
      const expectedStats = {};

      for (const group of myGroups) {
        const gamesSnap = await getDocs(collection(db, 'groups', group.id, 'games'));
        const allGames = gamesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const auditSnap = await getDocs(collection(db, 'groups', group.id, 'auditLog'));
        const auditByGameId = {};
        auditSnap.docs.forEach((d) => {
          const event = { id: d.id, ...d.data() };
          if (!event.targetId) return;
          if (!auditByGameId[event.targetId]) auditByGameId[event.targetId] = [];
          auditByGameId[event.targetId].push(event);
        });

        // ── 1. Jogos duplicados (scheduled com mesmo date+time) ──────────────
        const scheduledByKey = {};
        allGames
          .filter((g) => g.status === 'scheduled')
          .forEach((g) => {
            const key = `${g.date}|${g.time}`;
            if (!scheduledByKey[key]) scheduledByKey[key] = [];
            scheduledByKey[key].push(g);
          });

        Object.entries(scheduledByKey).forEach(([key, gms]) => {
          if (gms.length < 2) return;
          const sorted = [...gms].sort(
            (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
          );
          const [date, time] = key.split('|');
          dupGames.push({ group, date, time, keep: sorted[0], remove: sorted.slice(1) });
        });

        // ── 2. ratingHistory com gameId duplicado ────────────────────────────
        const finishedGames = allGames.filter((g) => g.status === 'finished');
        const playerIds = new Set();
        finishedGames.forEach((g) => {
          (g.confirmedIds || []).forEach((id) => {
            if (!id.startsWith('guest_')) playerIds.add(id);
          });
        });

        // Acumula stats esperadas (jogos + gols + assistências) a partir dos jogos finalizados
        finishedGames.forEach((g) => {
          const ps = g.playerStats || {};
          (g.confirmedIds || []).forEach((pid) => {
            if (pid.startsWith('guest_')) return;
            if (!expectedStats[pid]) expectedStats[pid] = { games: 0, goals: 0, assists: 0 };
            expectedStats[pid].games += 1;
            const st = ps[pid] || { goals: 0, assists: 0 };
            expectedStats[pid].goals += st.goals || 0;
            expectedStats[pid].assists += st.assists || 0;
          });
        });

        finishedGames.forEach((game) => {
          const statsSummary = summarizePlayerStats(game.playerStats || {});
          const ownGoals = game.ownGoals || { teamA: 0, teamB: 0 };
          const scoreTotal = (game.score?.teamA || 0) + (game.score?.teamB || 0);
          const ownGoalsTotal = (ownGoals.teamA || 0) + (ownGoals.teamB || 0);
          const votesCount = Object.keys(game.votes || {}).length;
          const hasSignal = scoreTotal > 0 || ownGoalsTotal > 0 || votesCount > 0;
          const looksMissing = hasSignal && statsSummary.entriesCount === 0;
          if (!looksMissing) return;

          const auditTrail = (auditByGameId[game.id] || [])
            .filter((event) => ['game.voting_started', 'game.finalized'].includes(event.action))
            .sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
          const votingAudit = auditTrail.find((event) => event.action === 'game.voting_started') || null;
          const finalizedAudit = [...auditTrail].reverse().find((event) => event.action === 'game.finalized') || null;

          statLossSuspects.push({
            groupId: group.id,
            groupName: group.name,
            gameId: game.id,
            date: game.date,
            time: game.time,
            score: game.score || { teamA: 0, teamB: 0 },
            ownGoals,
            votesCount,
            confirmedCount: (game.confirmedIds || []).filter((id) => !id.startsWith('guest_')).length,
            statsSummary,
            rawPlayerStats: game.playerStats || {},
            votingAudit,
            finalizedAudit,
          });
        });

        for (const pid of playerIds) {
          if (scannedPlayers.has(pid)) continue;
          scannedPlayers.add(pid);

          if (!profileCache[pid]) {
            const snap = await getDoc(doc(db, 'users', pid, 'profile', 'data'));
            if (!snap.exists()) continue;
            const d = snap.data();
            profileCache[pid] = { history: d.ratingHistory || [], name: d.name || pid, communityRating: d.communityRating, gamesPlayed: d.gamesPlayed, totalGoals: d.totalGoals, totalAssists: d.totalAssists };
          }

          const { history, name, communityRating } = profileCache[pid];
          if (history.length === 0) continue;

          const seenIdx = {};
          history.forEach((entry, i) => {
            if (!entry.gameId) return;
            if (!seenIdx[entry.gameId]) seenIdx[entry.gameId] = [];
            seenIdx[entry.gameId].push(i);
          });

          const duplicates = Object.entries(seenIdx)
            .filter(([, indices]) => indices.length > 1)
            .map(([gameId, indices]) => ({
              gameId,
              entries: indices.map((i) => ({ ...history[i], _idx: i })),
            }));

          if (duplicates.length === 0) continue;

          const keepIndices = new Set(
            Object.values(seenIdx).map((indices) => indices[indices.length - 1])
          );
          history.forEach((_, i) => {
            const gid = history[i]?.gameId;
            if (!gid || seenIdx[gid].length === 1) keepIndices.add(i);
          });
          const cleanHistory = history.filter((_, i) => keepIndices.has(i));

          dupRatings.push({
            uid: pid,
            name,
            currentRating: communityRating,
            newRating: calcCommunityRating(cleanHistory),
            history,
            cleanHistory,
            duplicates,
          });
        }

        // ── 3. Discrepâncias de votos (race condition na finalização) ─────────
        const gamesWithVotes = finishedGames.filter(
          (g) => g.votes && Object.keys(g.votes).length > 0
        );

        for (const game of gamesWithVotes) {
          // Reconstruir votos por jogador a partir do doc do jogo (fonte da verdade)
          const votesByPlayer = {};
          Object.values(game.votes).forEach((voteSheet) => {
            Object.entries(voteSheet).forEach(([pid, val]) => {
              if (pid.startsWith('guest_')) return;
              if (!votesByPlayer[pid]) votesByPlayer[pid] = [];
              votesByPlayer[pid].push(Number(val));
            });
          });

          const discrepantPlayers = [];

          for (const [pid, correctVotes] of Object.entries(votesByPlayer)) {
            // Garantir perfil em cache
            if (!profileCache[pid]) {
              try {
                const snap = await getDoc(doc(db, 'users', pid, 'profile', 'data'));
                if (!snap.exists()) continue;
                const d = snap.data();
                profileCache[pid] = { history: d.ratingHistory || [], name: d.name || pid, communityRating: d.communityRating, gamesPlayed: d.gamesPlayed, totalGoals: d.totalGoals, totalAssists: d.totalAssists };
              } catch { continue; }
            }

            const { history, name, communityRating: currentCR } = profileCache[pid];
            const historyIdx = history.findIndex((h) => h.gameId === game.id);
            if (historyIdx === -1) continue; // jogo não está no histórico

            const entry = history[historyIdx];

            // Contar total de votos armazenados (válidos + outliers)
            const storedValid   = Object.values(entry.voteDistribution || {}).reduce((a, b) => a + b, 0);
            const storedOutlier = (entry.outlierVotes || []).length;
            const storedTotal   = storedValid + storedOutlier;
            const correctTotal  = correctVotes.length;

            if (storedTotal === correctTotal) continue; // ← sem discrepância

            // Recalcular com os votos corretos.
            // Só aplica filtro de outliers se o jogo foi finalizado com esse recurso ativo
            // (campo outlierFilterApplied=true no doc do jogo — presente a partir do Sprint N).
            // Jogos anteriores usavam média simples sem descarte.
            let validVotes, outlierVotes;
            if (game.outlierFilterApplied === true) {
              ({ validVotes, outlierVotes } = detectOutlierVotes(correctVotes));
            } else {
              validVotes = correctVotes;
              outlierVotes = [];
            }
            const correctDistribution = {};
            validVotes.forEach((v) => {
              const k = String(v);
              correctDistribution[k] = (correctDistribution[k] || 0) + 1;
            });
            const correctAvg = validVotes.length > 0
              ? Math.round((validVotes.reduce((a, b) => a + b, 0) / validVotes.length) * 10) / 10
              : entry.rating;

            discrepantPlayers.push({
              uid: pid,
              name,
              storedTotal,
              correctTotal,
              storedAvg: entry.rating,
              correctAvg,
              correctDistribution,
              correctOutliers: outlierVotes,
              historyIdx,
              history,
              currentCR,
            });
          }

          if (discrepantPlayers.length > 0) {
            voteDiscrepancies.push({
              groupName: group.name,
              gameId: game.id,
              groupId: group.id,
              date: game.date,
              players: discrepantPlayers,
            });
          }
        }
      }

      // ── 4. Discrepâncias de stats (gamesPlayed/totalGoals/totalAssists) ────
      const statDiscrepancies = [];
      for (const [pid, expected] of Object.entries(expectedStats)) {
        // Garantir perfil em cache
        if (!profileCache[pid]) {
          try {
            const snap = await getDoc(doc(db, 'users', pid, 'profile', 'data'));
            if (!snap.exists()) continue;
            const d = snap.data();
            profileCache[pid] = { history: d.ratingHistory || [], name: d.name || pid, communityRating: d.communityRating, gamesPlayed: d.gamesPlayed, totalGoals: d.totalGoals, totalAssists: d.totalAssists };
          } catch { continue; }
        }
        const cache = profileCache[pid];
        const currentGames   = cache.gamesPlayed ?? null;
        const currentGoals   = cache.totalGoals ?? null;
        const currentAssists = cache.totalAssists ?? null;
        // Há discrepância se algum dos 3 está diferente do esperado
        // (null/undefined também conta — perfil nunca recebeu o contador)
        const gamesDiff   = (currentGames   ?? 0) !== expected.games;
        const goalsDiff   = (currentGoals   ?? 0) !== expected.goals;
        const assistsDiff = (currentAssists ?? 0) !== expected.assists;
        if (!gamesDiff && !goalsDiff && !assistsDiff) continue;
        statDiscrepancies.push({
          uid: pid,
          name: cache.name,
          currentGames, currentGoals, currentAssists,
          expectedGames: expected.games,
          expectedGoals: expected.goals,
          expectedAssists: expected.assists,
        });
      }

      statLossSuspects.sort((a, b) => {
        const left = (b.date || '') + ' ' + (b.time || '');
        const right = (a.date || '') + ' ' + (a.time || '');
        return left.localeCompare(right);
      });
      setResults({ dupGames, dupRatings, voteDiscrepancies, statDiscrepancies, statLossSuspects });
    } catch (err) {
      logger.error('Scan error:', err);
    } finally {
      setScanning(false);
    }
  }, [myGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fix jogos duplicados ──────────────────────────────────────────────────

  const handleDeleteGame = async (groupId, gameId, label) => {
    try {
      await deleteDoc(doc(db, 'groups', groupId, 'games', gameId));
      setFixLog((l) => [...l, `✅ Jogo excluído: ${label} (${gameId})`]);
      setResults((r) => ({
        ...r,
        dupGames: r.dupGames
          .map((d) => ({ ...d, remove: d.remove.filter((g) => g.id !== gameId) }))
          .filter((d) => d.remove.length > 0),
      }));
    } catch (err) {
      setFixLog((l) => [...l, `❌ Erro ao excluir ${gameId}: ${err.message}`]);
    }
  };

  // ─── Fix ratingHistory duplicado ───────────────────────────────────────────

  const handleFixPlayer = async (player) => {
    setFixing(true);
    try {
      const profileRef = doc(db, 'users', player.uid, 'profile', 'data');
      await updateDoc(profileRef, {
        ratingHistory: player.cleanHistory,
        communityRating: player.newRating,
      });
      setFixLog((l) => [
        ...l,
        `✅ ${player.name}: rating ${player.currentRating} → ${player.newRating} (${player.cleanHistory.length} jogos, removidas ${player.history.length - player.cleanHistory.length} entradas)`,
      ]);
      setResults((r) => ({
        ...r,
        dupRatings: r.dupRatings.filter((p) => p.uid !== player.uid),
      }));
    } catch (err) {
      setFixLog((l) => [...l, `❌ Erro ao corrigir ${player.name}: ${err.message}`]);
    } finally {
      setFixing(false);
    }
  };

  const handleFixAllRatings = async () => {
    setFixing(true);
    for (const player of results.dupRatings) {
      await handleFixPlayer(player);
    }
    setFixing(false);
  };

  // ─── Fix discrepância de votos ─────────────────────────────────────────────

  const handleFixVoteDiscrepancy = async (discrepancy, player) => {
    setFixing(true);
    try {
      const profileRef = doc(db, 'users', player.uid, 'profile', 'data');

      // Re-lê o perfil FRESCO do Firestore — evita sobrescrever com cache
      // desatualizado caso o perfil tenha sido alterado entre o scan e o clique.
      const freshSnap = await getDoc(profileRef);
      if (!freshSnap.exists()) {
        setFixLog((l) => [...l, `❌ ${player.name}: perfil não encontrado no Firestore`]);
        return;
      }
      const freshHistory = freshSnap.data().ratingHistory || [];
      const freshIdx = freshHistory.findIndex((h) => h.gameId === discrepancy.gameId);
      if (freshIdx === -1) {
        setFixLog((l) => [...l, `❌ ${player.name}: jogo ${discrepancy.gameId} não encontrado no histórico`]);
        return;
      }

      // Atualizar só a entrada do jogo afetado
      const newHistory = [...freshHistory];
      newHistory[freshIdx] = {
        ...newHistory[freshIdx],
        rating:           player.correctAvg,
        voteDistribution: player.correctDistribution,
        outlierVotes:     player.correctOutliers,
      };
      const newCR = calcCommunityRating(newHistory);

      await updateDoc(profileRef, {
        ratingHistory:   newHistory,
        communityRating: newCR,
      });

      setFixLog((l) => [
        ...l,
        `✅ ${player.name} (${fmtDate(discrepancy.date)}): ${player.storedTotal} → ${player.correctTotal} votos · média ${player.storedAvg} → ${player.correctAvg} · CR ${player.currentCR} → ${newCR}`,
      ]);

      // Remover da UI
      setResults((r) => {
        const newDisc = r.voteDiscrepancies.map((d) => {
          if (d.gameId !== discrepancy.gameId) return d;
          return { ...d, players: d.players.filter((p) => p.uid !== player.uid) };
        }).filter((d) => d.players.length > 0);
        return { ...r, voteDiscrepancies: newDisc };
      });
    } catch (err) {
      setFixLog((l) => [...l, `❌ Erro ao corrigir ${player.name}: ${err.message}`]);
    } finally {
      setFixing(false);
    }
  };

  const handleFixAllVoteDiscrepancies = async () => {
    setFixing(true);
    for (const disc of results.voteDiscrepancies) {
      for (const player of disc.players) {
        await handleFixVoteDiscrepancy(disc, player);
      }
    }
    setFixing(false);
  };

  // ─── Fix discrepância de stats (gamesPlayed/totalGoals/totalAssists) ──────

  const handleFixStatDiscrepancy = async (player) => {
    setFixing(true);
    try {
      const profileRef = doc(db, 'users', player.uid, 'profile', 'data');
      await updateDoc(profileRef, {
        gamesPlayed: player.expectedGames,
        totalGoals: player.expectedGoals,
        totalAssists: player.expectedAssists,
      });
      setFixLog((l) => [
        ...l,
        `✅ ${player.name}: jogos ${player.currentGames ?? '–'}→${player.expectedGames}, gols ${player.currentGoals ?? '–'}→${player.expectedGoals}, assist. ${player.currentAssists ?? '–'}→${player.expectedAssists}`,
      ]);
      setResults((r) => ({
        ...r,
        statDiscrepancies: r.statDiscrepancies.filter((p) => p.uid !== player.uid),
      }));
    } catch (err) {
      setFixLog((l) => [...l, `❌ Erro ao corrigir ${player.name}: ${err.message}`]);
    } finally {
      setFixing(false);
    }
  };

  const handleFixAllStatDiscrepancies = async () => {
    setFixing(true);
    for (const player of results.statDiscrepancies) {
      await handleFixStatDiscrepancy(player);
    }
    setFixing(false);
  };

  const toggleExpand = (uid) =>
    setExpandedPlayers((e) => ({ ...e, [uid]: !e[uid] }));

  // ─── Vote viewer handlers ──────────────────────────────────────────────────

  const handleVvGroupChange = useCallback(async (groupId) => {
    setVvGroupId(groupId);
    setVvGameId('');
    setVvData(null);
    setVvGames([]);
    if (!groupId) return;
    setVvGamesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'groups', groupId, 'games'));
      const finished = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((g) => g.status === 'finished' && g.votes && Object.keys(g.votes).length > 0)
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setVvGames(finished);
    } catch (err) {
      logger.error('VV group change error:', err);
    } finally {
      setVvGamesLoading(false);
    }
  }, []);

  const handleVvLoad = useCallback(async () => {
    if (!vvGroupId || !vvGameId) return;
    setVvLoading(true);
    setVvData(null);
    try {
      const gameSnap = await getDoc(doc(db, 'groups', vvGroupId, 'games', vvGameId));
      if (!gameSnap.exists()) { setVvLoading(false); return; }
      const game = { id: gameSnap.id, ...gameSnap.data() };

      // Invert votes: playerId → [{ voterId, rating }]
      const votesByPlayer = {};
      Object.entries(game.votes || {}).forEach(([voterId, voteSheet]) => {
        Object.entries(voteSheet).forEach(([playerId, rating]) => {
          if (!votesByPlayer[playerId]) votesByPlayer[playerId] = [];
          votesByPlayer[playerId].push({ voterId, rating: Number(rating) });
        });
      });

      // Collect all UIDs that need name resolution
      const allUids = new Set([
        ...Object.keys(votesByPlayer),
        ...Object.keys(game.votes || {}),
      ]);

      const nameCache = {};
      await Promise.all([...allUids].map(async (uid) => {
        if (uid.startsWith('guest_'))     { nameCache[uid] = 'Convidado'; return; }
        if (uid.startsWith('seed_voter')) { nameCache[uid] = uid; return; }
        try {
          const s = await getDoc(doc(db, 'users', uid, 'profile', 'data'));
          nameCache[uid] = s.exists() ? (s.data().name || uid) : uid;
        } catch { nameCache[uid] = uid; }
      }));

      // Build per-player breakdown sorted by avg desc
      const playerBreakdowns = Object.entries(votesByPlayer)
        .map(([playerId, votes]) => ({
          playerId,
          playerName: nameCache[playerId] || playerId,
          votes: [...votes]
            .map((v) => ({ ...v, voterName: nameCache[v.voterId] || v.voterId }))
            .sort((a, b) => b.rating - a.rating),
          avg: votes.reduce((s, v) => s + v.rating, 0) / votes.length,
        }))
        .sort((a, b) => b.avg - a.avg);

      setVvData({
        game,
        groupName: myGroups.find((g) => g.id === vvGroupId)?.name || vvGroupId,
        playerBreakdowns,
      });
    } catch (err) {
      logger.error('VV load error:', err);
    } finally {
      setVvLoading(false);
    }
  }, [vvGroupId, vvGameId, myGroups]);

  const totalVoteDiscrepancyPlayers = results?.voteDiscrepancies?.reduce(
    (sum, d) => sum + d.players.length, 0
  ) ?? 0;

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!profile?.superAdmin) {
    return (
      <>
        <Header showBack onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <p className="text-slate-400 text-sm">Acesso restrito.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header showBack onBack={() => navigate(-1)} />
      <div className="flex-1 overflow-y-auto pb-10">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

          {/* Header */}
          <div>
            <h1 className="text-xl font-bold text-slate-800">Correção de Dados</h1>
            <p className="text-sm text-slate-500 mt-1">
              Detecta jogos duplicados, histórico duplicado e discrepâncias de votos.
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Grupos escaneados: {myGroups.map((g) => g.name).join(', ')}
            </p>
          </div>

          {/* ── Seed (somente dev) ── */}
          {isDev && (
            <section className="border-2 border-dashed border-amber-300 rounded-xl p-4 space-y-3 bg-amber-50">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-amber-600" />
                <h2 className="font-bold text-amber-800">Dados de teste (DEV)</h2>
                <span className="ml-auto text-[10px] font-bold px-2 py-0.5 bg-amber-300 text-amber-900 rounded-full">DEV ONLY</span>
              </div>
              <p className="text-xs text-amber-700">
                Cria um grupo <strong>[SEED] Pelada de Teste</strong> com 5 jogos em estados diferentes.
              </p>
              <Button onClick={handleSeed} disabled={seedRunning} className="w-full bg-amber-500 hover:bg-amber-600 text-white border-0">
                <FlaskConical className="w-4 h-4" />
                {seedRunning ? 'Gerando dados…' : 'Gerar dados de teste'}
              </Button>
              {seedLog.length > 0 && (
                <div className="bg-slate-900 rounded-xl p-3 space-y-0.5">
                  {seedLog.map((line, i) => (
                    <p key={i} className={`text-xs font-mono ${line.startsWith('❌') ? 'text-red-400' : line.startsWith('🎉') ? 'text-emerald-400' : line === '' ? 'h-2' : 'text-slate-300'}`}>
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Visualizar Votos ── */}
          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-500" />
              <h2 className="font-bold text-slate-700">Visualizar Votos por Jogo</h2>
            </div>
            <p className="text-xs text-slate-400">
              Selecione um grupo e um jogo finalizado para ver quem deu cada nota a cada jogador.
            </p>

            {/* Group selector */}
            <div className="space-y-2">
              <select
                value={vvGroupId}
                onChange={(e) => handleVvGroupChange(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-indigo-400 bg-white"
              >
                <option value="">— Selecione um grupo —</option>
                {myGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>

              {/* Game selector */}
              {vvGroupId && (
                <div className="flex gap-2">
                  <select
                    value={vvGameId}
                    onChange={(e) => setVvGameId(e.target.value)}
                    disabled={vvGamesLoading}
                    className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-indigo-400 bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {vvGamesLoading ? 'Carregando jogos…' : vvGames.length === 0 ? 'Nenhum jogo com votos' : '— Selecione um jogo —'}
                    </option>
                    {vvGames.map((g) => (
                      <option key={g.id} value={g.id}>
                        {fmtDate(g.date)} {g.time} — {Object.keys(g.votes || {}).length} votantes
                      </option>
                    ))}
                  </select>
                  <Button
                    onClick={handleVvLoad}
                    disabled={!vvGameId || vvLoading}
                    className="shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white border-0 px-4"
                  >
                    {vvLoading ? '…' : 'Ver'}
                  </Button>
                </div>
              )}
            </div>

            {/* Results */}
            {vvData && (
              <div className="space-y-3 pt-1">
                <div className="text-xs text-slate-500 font-medium border-t border-slate-100 pt-3">
                  {vvData.groupName} — {fmtDate(vvData.game.date)}{' '}
                  <span className="text-slate-400">
                    · {vvData.playerBreakdowns.length} jogador{vvData.playerBreakdowns.length !== 1 ? 'es' : ''} avaliados
                    · {Object.keys(vvData.game.votes || {}).length} votantes
                  </span>
                </div>

                {vvData.playerBreakdowns.map((pb, pi) => (
                  <div key={pb.playerId} className="bg-slate-50 rounded-xl p-3 space-y-2">
                    {/* Player header */}
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-slate-800 text-sm">{pb.playerName}</div>
                      <div className="text-sm font-bold text-indigo-600">
                        ★ {pb.avg.toFixed(1)}
                        <span className="text-xs font-normal text-slate-400 ml-1">({pb.votes.length} voto{pb.votes.length !== 1 ? 's' : ''})</span>
                      </div>
                    </div>

                    {/* Vote rows */}
                    <div className="space-y-1">
                      {pb.votes.map((v, vi) => {
                        const pct = ((v.rating - 1) / 9) * 100;
                        const barColor = v.rating >= 8 ? 'bg-emerald-400' : v.rating >= 5 ? 'bg-blue-400' : 'bg-red-400';
                        return (
                          <div key={vi} className="flex items-center gap-2 text-xs">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white shrink-0 ${
                              pi === 0 ? 'bg-amber-400' : pi === 1 ? 'bg-slate-400' : pi === 2 ? 'bg-orange-400' : 'bg-indigo-400'
                            }`}>
                              {v.rating}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-slate-600 truncate">{v.voterName}</div>
                              <div className="w-full bg-slate-200 rounded-full h-1 mt-0.5">
                                <div className={`h-1 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Scan button */}
          <Button onClick={handleScan} disabled={scanning} className="w-full">
            <Search className="w-4 h-4" />
            {scanning ? 'Escaneando…' : 'Escanear agora'}
          </Button>

          {results && (
            <>
              {/* ── 0. Investigar stats perdidas ── */}
              <section>
                <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-500" />
                  Investigar gols e assistencias ({results.statLossSuspects?.length || 0} jogo{(results.statLossSuspects?.length || 0) !== 1 ? 's' : ''})
                </h2>
                <p className="text-xs text-slate-400 mb-3">
                  Lista jogos finalizados com placar, votos ou gols contra, mas sem playerStats salvos no documento final.
                  Se houver snapshot na auditoria, ele aparece abaixo para ajudar a confirmar se os dados existiam antes de sumir.
                </p>

                {(results.statLossSuspects?.length || 0) === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> Nenhum jogo suspeito encontrado.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.statLossSuspects.map((item) => {
                      const votingSnapshot = item.votingAudit?.details?.playerStatsSnapshot || {};
                      const finalizedSnapshot = item.finalizedAudit?.details?.playerStatsSnapshot || {};
                      const votingSummary = item.votingAudit?.details?.statsSummary || null;
                      const finalizedSummary = item.finalizedAudit?.details?.statsSummary || null;
                      return (
                        <div key={item.gameId} className="bg-white border border-rose-200 rounded-xl p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-bold text-slate-800">{item.groupName}</div>
                              <div className="text-xs text-slate-500">{fmtDate(item.date)} {item.time || ''} � placar {fmtScore(item.score)} � {item.votesCount} voto{item.votesCount !== 1 ? 's' : ''}</div>
                              <div className="font-mono text-[10px] text-slate-400 mt-1">{item.gameId}</div>
                            </div>
                            <div className="text-right text-[11px] text-slate-500 shrink-0">
                              <div>{item.confirmedCount} participante{item.confirmedCount !== 1 ? 's' : ''}</div>
                              <div>gols contra: {(item.ownGoals?.teamA || 0) + (item.ownGoals?.teamB || 0)}</div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                              <div className="font-bold text-slate-700 mb-1">Documento final</div>
                              <div className="text-slate-500">Entradas: <span className="font-mono">{item.statsSummary.entriesCount}</span></div>
                              <div className="text-slate-500">Jogadores c/ stats: <span className="font-mono">{item.statsSummary.nonZeroPlayers}</span></div>
                              <div className="text-slate-500">Gols: <span className="font-mono">{item.statsSummary.totalGoals}</span></div>
                              <div className="text-slate-500">Assist.: <span className="font-mono">{item.statsSummary.totalAssists}</span></div>
                            </div>
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                              <div className="font-bold text-indigo-700 mb-1">Auditoria ao iniciar votacao</div>
                              {item.votingAudit ? (
                                <>
                                  <div className="text-indigo-700">Entradas: <span className="font-mono">{votingSummary?.playersWithStats ?? Object.keys(votingSnapshot).length}</span></div>
                                  <div className="text-indigo-700">Gols: <span className="font-mono">{votingSummary?.totalGoals ?? 0}</span></div>
                                  <div className="text-indigo-700">Assist.: <span className="font-mono">{votingSummary?.totalAssists ?? 0}</span></div>
                                  <div className="text-indigo-500 mt-2 break-all">Snapshot: {JSON.stringify(votingSnapshot)}</div>
                                </>
                              ) : (
                                <div className="text-slate-400">Sem snapshot antigo.</div>
                              )}
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                              <div className="font-bold text-emerald-700 mb-1">Auditoria ao finalizar</div>
                              {item.finalizedAudit ? (
                                <>
                                  <div className="text-emerald-700">Entradas: <span className="font-mono">{finalizedSummary?.playersWithStats ?? Object.keys(finalizedSnapshot).length}</span></div>
                                  <div className="text-emerald-700">Gols: <span className="font-mono">{finalizedSummary?.totalGoals ?? 0}</span></div>
                                  <div className="text-emerald-700">Assist.: <span className="font-mono">{finalizedSummary?.totalAssists ?? 0}</span></div>
                                  <div className="text-emerald-500 mt-2 break-all">Snapshot: {JSON.stringify(finalizedSnapshot)}</div>
                                </>
                              ) : (
                                <div className="text-slate-400">Sem snapshot antigo.</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* ── 1. Jogos duplicados ── */}
              <section>
                <h2 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-red-500" />
                  Jogos duplicados ({results.dupGames.length})
                </h2>
                {results.dupGames.length === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> Nenhum jogo duplicado encontrado.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.dupGames.map((d, i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                        <div className="text-sm font-bold text-slate-700">
                          {d.group.name} — {fmtDate(d.date)} {d.time}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                            <span className="text-emerald-700 font-medium">Manter</span>
                            <span className="text-slate-500 font-mono">{d.keep.id}</span>
                            <span className="text-slate-400 ml-auto">{d.keep.confirmedIds?.length ?? 0} confirmados</span>
                          </div>
                          {d.remove.map((g) => (
                            <div key={g.id} className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                              <span className="text-red-700 font-medium">Duplicata</span>
                              <span className="text-slate-500 font-mono">{g.id}</span>
                              <span className="text-slate-400">{g.confirmedIds?.length ?? 0} confirmados</span>
                              <button
                                onClick={() => handleDeleteGame(d.group.id, g.id, `${d.group.name} ${d.date} ${d.time}`)}
                                className="ml-auto text-red-600 font-bold hover:text-red-800 text-xs underline"
                              >
                                Excluir
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ── 2. Histórico duplicado ── */}
              <section>
                <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Histórico duplicado ({results.dupRatings.length} jogadores)
                </h2>

                {results.dupRatings.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-xs text-amber-800 space-y-1">
                    <p className="font-bold">⚠️ Antes de corrigir ratings de outros jogadores:</p>
                    <p>1. Firebase Console → Firestore → Regras → <code className="bg-amber-100 px-1 rounded">allow write: if request.auth != null;</code></p>
                    <p>2. Corrija abaixo → depois restaure a regra original.</p>
                  </div>
                )}

                {results.dupRatings.length === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> Nenhum histórico duplicado encontrado.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.dupRatings.map((player) => (
                      <div key={player.uid} className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{player.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              communityRating:{' '}
                              <span className="text-red-600 font-mono line-through">{player.currentRating ?? '–'}</span>
                              {' → '}
                              <span className="text-emerald-600 font-mono font-bold">{player.newRating ?? '–'}</span>
                              {' '}
                              <span className="text-slate-400">({player.history.length} → {player.cleanHistory.length} entradas)</span>
                            </div>
                          </div>
                          <Button variant="danger" onClick={() => handleFixPlayer(player)} disabled={fixing} className="text-xs py-1.5 px-3">
                            Corrigir
                          </Button>
                        </div>
                        <button
                          onClick={() => toggleExpand(player.uid)}
                          className="text-xs text-blue-600 font-medium flex items-center gap-1 mb-2 hover:underline"
                        >
                          {expandedPlayers[player.uid] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {player.duplicates.length} entrada{player.duplicates.length !== 1 ? 's' : ''} duplicada{player.duplicates.length !== 1 ? 's' : ''}
                        </button>
                        {expandedPlayers[player.uid] && (
                          <div className="space-y-2">
                            {player.duplicates.map((dup) => (
                              <div key={dup.gameId} className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                                <div className="font-mono text-slate-500 mb-1">gameId: {dup.gameId}</div>
                                {dup.entries.map((e, ei) => {
                                  const isLast = ei === dup.entries.length - 1;
                                  return (
                                    <div key={ei} className={`flex items-center gap-2 px-2 py-1 rounded ${isLast ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                                      {isLast ? <CheckCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" /> : <Trash2 className="w-3 h-3 text-red-500 flex-shrink-0" />}
                                      <span className={isLast ? 'text-emerald-700 font-medium' : 'text-red-600'}>{isLast ? 'Manter' : 'Remover'}</span>
                                      <span className="text-slate-500">{fmtDate(e.date)}</span>
                                      <span className="ml-auto font-bold text-slate-700">⭐ {e.rating}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {results.dupRatings.length > 1 && (
                      <Button variant="danger" className="w-full" onClick={handleFixAllRatings} disabled={fixing}>
                        {fixing ? 'Corrigindo…' : `Corrigir todos (${results.dupRatings.length} jogadores)`}
                      </Button>
                    )}
                  </div>
                )}
              </section>

              {/* ── 3. Discrepâncias de votos ── */}
              <section>
                <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-blue-500" />
                  Discrepâncias de votos ({totalVoteDiscrepancyPlayers} jogador{totalVoteDiscrepancyPlayers !== 1 ? 'es' : ''})
                </h2>
                <p className="text-xs text-slate-400 mb-3">
                  Detecta quando o número de votos gravado no histórico é diferente do que está no jogo
                  — causado por race condition na finalização automática.
                </p>

                {totalVoteDiscrepancyPlayers > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-800 space-y-1">
                    <p className="font-bold">ℹ️ Antes de corrigir perfis de outros jogadores:</p>
                    <p>Firebase Console → Firestore → Regras → <code className="bg-blue-100 px-1 rounded">allow write: if request.auth != null;</code> → corrija → restaure.</p>
                  </div>
                )}

                {totalVoteDiscrepancyPlayers === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> Nenhuma discrepância de votos encontrada.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.voteDiscrepancies.map((disc) => (
                      <div key={disc.gameId} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                        {/* Cabeçalho do jogo */}
                        <div>
                          <div className="text-sm font-bold text-slate-700">{disc.groupName}</div>
                          <div className="text-xs text-slate-500">{fmtDate(disc.date)}</div>
                          <div className="font-mono text-[10px] text-slate-400 mt-0.5">{disc.gameId}</div>
                        </div>

                        {/* Jogadores afetados */}
                        {disc.players.map((player) => (
                          <div key={player.uid} className="bg-slate-50 rounded-xl p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-bold text-slate-800 text-sm">{player.name}</div>
                                <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                                  <div>
                                    Votos:{' '}
                                    <span className="text-red-500 font-mono line-through">{player.storedTotal}</span>
                                    {' → '}
                                    <span className="text-emerald-600 font-mono font-bold">{player.correctTotal}</span>
                                  </div>
                                  <div>
                                    Média válida:{' '}
                                    <span className="text-red-500 font-mono line-through">{player.storedAvg}</span>
                                    {' → '}
                                    <span className="text-emerald-600 font-mono font-bold">{player.correctAvg}</span>
                                  </div>
                                </div>
                              </div>
                              <Button
                                onClick={() => handleFixVoteDiscrepancy(disc, player)}
                                disabled={fixing}
                                className="text-xs py-1.5 px-3 shrink-0"
                              >
                                Corrigir
                              </Button>
                            </div>

                            {/* Distribuição correta */}
                            <div className="text-xs text-slate-500">
                              <span className="font-medium">Distribuição correta: </span>
                              {Object.entries(player.correctDistribution)
                                .sort(([a], [b]) => Number(b) - Number(a))
                                .map(([nota, qtd]) => `${nota}×${qtd}`)
                                .join('  ')}
                              {player.correctOutliers.length > 0 && (
                                <span className="ml-2 text-slate-400">
                                  (descartados: {player.correctOutliers.join(', ')})
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* Corrigir todos */}
                    {totalVoteDiscrepancyPlayers > 1 && (
                      <Button className="w-full" onClick={handleFixAllVoteDiscrepancies} disabled={fixing}>
                        <RefreshCw className="w-4 h-4" />
                        {fixing ? 'Corrigindo…' : `Corrigir todos (${totalVoteDiscrepancyPlayers} jogadores)`}
                      </Button>
                    )}
                  </div>
                )}
              </section>

              {/* ── 4. Discrepâncias de stats (gamesPlayed/gols/assistências) ── */}
              <section>
                <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-purple-500" />
                  Estatísticas dos jogadores ({results.statDiscrepancies?.length || 0})
                </h2>
                <p className="text-xs text-slate-400 mb-3">
                  Detecta jogadores cujos contadores no perfil (jogos, gols, assistências) não batem
                  com o que está nos jogos finalizados — geralmente jogos antigos finalizados antes
                  desses contadores existirem, ou participações como externo.
                </p>

                {(results.statDiscrepancies?.length || 0) > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3 text-xs text-purple-800 space-y-1">
                    <p className="font-bold">ℹ️ Antes de corrigir perfis de outros jogadores:</p>
                    <p>Firebase Console → Firestore → Regras → <code className="bg-purple-100 px-1 rounded">allow write: if request.auth != null;</code> → corrija → restaure.</p>
                  </div>
                )}

                {(results.statDiscrepancies?.length || 0) === 0 ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" /> Todas as estatísticas estão consistentes.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.statDiscrepancies.map((player) => (
                      <div key={player.uid} className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-800 text-sm">{player.name}</div>
                            <div className="text-xs text-slate-500 mt-1 grid grid-cols-3 gap-2">
                              <div>
                                <div className="text-[10px] uppercase text-slate-400">Jogos</div>
                                <div>
                                  <span className={`font-mono ${(player.currentGames ?? 0) !== player.expectedGames ? 'text-red-500 line-through' : 'text-slate-500'}`}>{player.currentGames ?? '–'}</span>
                                  {(player.currentGames ?? 0) !== player.expectedGames && (
                                    <span className="text-emerald-600 font-mono font-bold ml-1">→ {player.expectedGames}</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase text-slate-400">Gols</div>
                                <div>
                                  <span className={`font-mono ${(player.currentGoals ?? 0) !== player.expectedGoals ? 'text-red-500 line-through' : 'text-slate-500'}`}>{player.currentGoals ?? '–'}</span>
                                  {(player.currentGoals ?? 0) !== player.expectedGoals && (
                                    <span className="text-emerald-600 font-mono font-bold ml-1">→ {player.expectedGoals}</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase text-slate-400">Assist.</div>
                                <div>
                                  <span className={`font-mono ${(player.currentAssists ?? 0) !== player.expectedAssists ? 'text-red-500 line-through' : 'text-slate-500'}`}>{player.currentAssists ?? '–'}</span>
                                  {(player.currentAssists ?? 0) !== player.expectedAssists && (
                                    <span className="text-emerald-600 font-mono font-bold ml-1">→ {player.expectedAssists}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleFixStatDiscrepancy(player)}
                            disabled={fixing}
                            className="text-xs py-1.5 px-3 shrink-0"
                          >
                            Corrigir
                          </Button>
                        </div>
                      </div>
                    ))}

                    {results.statDiscrepancies.length > 1 && (
                      <Button className="w-full" onClick={handleFixAllStatDiscrepancies} disabled={fixing}>
                        <RefreshCw className="w-4 h-4" />
                        {fixing ? 'Corrigindo…' : `Corrigir todos (${results.statDiscrepancies.length} jogadores)`}
                      </Button>
                    )}
                  </div>
                )}
              </section>

              {/* ── Log ── */}
              {fixLog.length > 0 && (
                <section>
                  <h2 className="font-bold text-slate-700 mb-2 text-sm">Log de operações</h2>
                  <div className="bg-slate-900 rounded-xl p-4 space-y-1">
                    {fixLog.map((line, i) => (
                      <p key={i} className="text-xs font-mono text-slate-300">{line}</p>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FixDuplicatesPage;









